import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Headline, Tag, Text } from '@oneaudi/unified-web-alpha-components';
import useAnimations from '../../../hooks/useAnimations';
import { device } from '../../../styles/devices';
import ExposedHeader from '../ExposedHeader';
import Offers from '../Offers';
import {
  MobileWrapper,
  VehicleInfoContainer,
  Title,
  OfferType,
  CreditBonus,
  CreditBonusDescription,
  Trimline,
  VehicleImage,
  ImageWrapper,
  VehicleName,
} from '../VehicleInfo.styles';
import FallbackCarDefault from '../../../assets/fallback_car_default.png';
import { OffersTypes } from '../../../types';
import { numberFormatter } from '../../../functions/priceFormat';

interface OfferNoVehicleProps {
  offers: OffersTypes;
  isOfferPage: boolean;
}

const OfferNoVehicle: React.FC<OfferNoVehicleProps> = ({ offers, isOfferPage }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const isTablet = useMediaQuery({ query: device.tablet });
  const [isOpen, setIsOpen] = useState(false);
  const dropdownHeigh = 464;

  const image = offers?.image ? offers.image : FallbackCarDefault;
  const modelName = offers?.modelName;
  const trimName = offers?.trimName;

  const renderVehicleInfo = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <>
          <VehicleName data-testid="vehicle-info-name">{modelName}</VehicleName>
          <Trimline>{trimName}</Trimline>
        </>
      );
    }

    return (
      <>
        <Headline
          data-testid="vehicle-info-name"
          margin={['8px', 0, 0, 0]}
          variant={{ type: 'headline', order: '3', weight: 'normal' }}
          tag="h2"
        >
          {modelName}
        </Headline>
        <Text
          margin={['12px', 0, 0, 0]}
          variant={{ type: 'copy', order: '1', style: 'normal' }}
          tag="p"
        >
          {trimName}
        </Text>
      </>
    );
  }, [modelName, trimName]);

  const renderCreditBonus = useMemo(() => {
    if (process.env.IS_LEGACY) {
      return (
        <>
          <CreditBonus>
            {numberFormatter(offers?.offerDetails.creditBonus, lang)}{' '}
            {t('digitalLeads.vehicle.offers.credit')}
          </CreditBonus>
          <CreditBonusDescription>
            {offers?.offerDetails.creditBonusDisclaimer}
          </CreditBonusDescription>
        </>
      );
    }

    return (
      <>
        <Headline
          margin={['24px', 0, 0, 0]}
          variant={{ type: 'headline', order: '4', weight: 'normal' }}
          tag="h2"
        >
          {numberFormatter(offers?.offerDetails.creditBonus, lang)}{' '}
          {t('digitalLeads.vehicle.offers.credit')}
        </Headline>
        <Text
          margin={['12px', 0, 0, 0]}
          variant={{ type: 'copy', order: '1', style: 'normal' }}
          tag="p"
        >
          {offers?.offerDetails.creditBonusDisclaimer}
        </Text>
      </>
    );
  }, [offers, lang, t]);

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(isOpen, dropdownHeigh);
  return (
    <>
      {dropdownAnimation(
        (props, item) =>
          item && (
            <MobileWrapper style={props}>
              <VehicleInfoContainer style={filtersAccordianAnimation} data-testid="vehicle-info">
                <ImageWrapper data-testid="vehicle-info-image">
                  <VehicleImage src={image} />
                </ImageWrapper>
                {renderVehicleInfo}
                {renderCreditBonus}
              </VehicleInfoContainer>
            </MobileWrapper>
          ),
      )}
      {!isTablet && (
        <ExposedHeader
          isOpened={isOpen}
          setIsOpened={setIsOpen}
          modelName={modelName}
          isOfferPage={isOfferPage}
          offers={offers}
        />
      )}
      {isTablet && (
        <VehicleInfoContainer data-testid="vehicle-info-tablet">
          <ImageWrapper data-testid="vehicle-info-image">
            <VehicleImage src={image} />
          </ImageWrapper>

          {process.env.IS_LEGACY ? (
            <Title paddingTop>{t('digitalLeads.vehicle.offerOfInterest')}</Title>
          ) : (
            <Headline
              margin={['24px', 0, 0, 0]}
              variant={{ type: 'headline', order: '4', weight: 'normal' }}
              tag="h2"
            >
              {t('digitalLeads.vehicle.offerOfInterest')}
            </Headline>
          )}

          {process.env.IS_LEGACY ? (
            <OfferType>{t(`digitalLeads.vehicle.offers.${offers?.offerType}`)}</OfferType>
          ) : (
            <Tag
              label={t(`digitalLeads.vehicle.offers.${offers?.offerType}`)}
              margin={[0, 0, 0, 0]}
              size="md"
              variant="read-only"
            />
          )}

          {renderVehicleInfo}

          {renderCreditBonus}
        </VehicleInfoContainer>
      )}
    </>
  );
};

export default OfferNoVehicle;
